import React, { useState } from 'react';
import {
  Plus, Trash2, ChevronDown, ChevronUp, Phone, Wifi,
  Radio, Tablet as TabletIcon, Wrench, Package, Minus, Sparkles, AlertTriangle, type LucideIcon,
} from 'lucide-react';
import {
  QuoteState, QuoteItem, QuoteDevice, Promo,
  SolutionType, PLAN_OPTIONS, DEFAULT_MRC, DEVICE_CATALOG,
  DeviceFinancingTerm, deviceMrcCalc, promoMonthlyCalc,
} from '../types';

interface Props {
  quote:    QuoteState;
  onChange: (q: QuoteState) => void;
}

const TYPE_ICONS: Record<SolutionType, LucideIcon> = {
  [SolutionType.VOICE]:       Phone,
  [SolutionType.INTERNET]:    Wifi,
  [SolutionType.IOT]:         Radio,
  [SolutionType.TABLET]:      TabletIcon,
  [SolutionType.PRO_INSTALL]: Wrench,
  [SolutionType.FEATURES]:    Sparkles,
};

const TYPE_COLORS: Record<SolutionType, string> = {
  [SolutionType.VOICE]:       'from-purple-500 to-violet-600',
  [SolutionType.INTERNET]:    'from-blue-500 to-cyan-600',
  [SolutionType.IOT]:         'from-emerald-500 to-teal-600',
  [SolutionType.TABLET]:      'from-amber-500 to-orange-600',
  [SolutionType.PRO_INSTALL]: 'from-rose-500 to-pink-600',
  [SolutionType.FEATURES]:    'from-sky-400 to-indigo-500',
};

const uid = () => Math.random().toString(36).slice(2, 9);

const newItem = (type: SolutionType = SolutionType.VOICE): QuoteItem => {
  const isFeatures = type === SolutionType.FEATURES;
  const plan = isFeatures ? '' : PLAN_OPTIONS[type][0];
  return {
    id:       uid(),
    type,
    planName: plan,
    lines:    1,
    mrc:      DEFAULT_MRC[plan] ?? 0,
    devices:  [],
  };
};

const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`rounded-2xl bg-[#0D0D1A] border border-white/8 p-5 ${className}`}>{children}</div>
);

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">{children}</label>
);

// --- Coverage Analysis data (sourced from Carrier Coverage Analysis 2026.json) ---
const COVERAGE_REGIONS: { states: string[]; primary: string; note: string; color: string }[] = [
  {
    states: ['NY', 'NJ', 'PA', 'MA', 'CT', 'ME', 'NH', 'VT', 'RI', 'DE', 'MD', 'DC'],
    primary: 'T-Mobile',
    note: 'Leader in 5G speed & availability in the Northeast.',
    color: 'from-pink-500 to-rose-600',
  },
  {
    states: ['TX', 'FL', 'GA', 'NC', 'SC', 'LA', 'MS', 'AL', 'AR', 'TN', 'KY', 'VA', 'WV', 'OK'],
    primary: 'AT&T',
    note: 'Strongest infrastructure & connection consistency in the South.',
    color: 'from-blue-500 to-cyan-600',
  },
  {
    states: ['IL', 'OH', 'MI', 'IN', 'WI', 'IA', 'MO', 'MN', 'ND', 'SD', 'NE', 'KS'],
    primary: 'Verizon',
    note: 'Primary Midwest carrier; best rural reliability.',
    color: 'from-red-500 to-orange-600',
  },
  {
    states: ['CA', 'AZ', 'NV', 'WA', 'OR', 'CO', 'UT', 'NM', 'ID', 'MT', 'WY'],
    primary: 'T-Mobile',
    note: 'Best 5G quality in the West; Verizon leads in reliability.',
    color: 'from-pink-500 to-rose-600',
  },
  {
    states: ['AK'],
    primary: 'AT&T',
    note: 'AT&T is dominant in Alaska.',
    color: 'from-blue-500 to-cyan-600',
  },
  {
    states: ['HI'],
    primary: 'Verizon',
    note: 'Verizon leads coverage statewide; T-Mobile leads in Honolulu 5G.',
    color: 'from-red-500 to-orange-600',
  },
];

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN',
  'IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH',
  'NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT',
  'VT','VA','WA','WV','WI','WY',
];

function getCoverageRec(state: string) {
  if (!state) return null;
  return COVERAGE_REGIONS.find(r => r.states.includes(state)) ?? null;
}

export const QuoteBuilder: React.FC<Props> = ({ quote, onChange }) => {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [sellerOpen, setSellerOpen] = useState(false);

  const update = (patch: Partial<QuoteState>) => onChange({ ...quote, ...patch });

  const addItem = (type: SolutionType) => update({ items: [...quote.items, newItem(type)] });

  const removeItem = (id: string) => update({ items: quote.items.filter(it => it.id !== id) });

  const updateItem = (id: string, patch: Partial<QuoteItem>) =>
    update({ items: quote.items.map(it => it.id === id ? { ...it, ...patch } : it) });

  const updatePlan = (id: string, planName: string, type: SolutionType) =>
    updateItem(id, { planName, mrc: DEFAULT_MRC[planName] ?? 0, type });

  const FINANCING_TERMS: DeviceFinancingTerm[] = ['FRP', '24', '30', '33', '36'];

  const coverageRec = getCoverageRec(quote.hqState ?? '');

  const addDevice = (itemId: string) => {
    const item = quote.items.find(i => i.id === itemId);
    if (!item) return;
    const dev: QuoteDevice = {
      id:            uid(),
      name:          DEVICE_CATALOG[0].name,
      cost:          DEVICE_CATALOG[0].cost,
      count:         1,
      financingTerm: 'FRP',
    };
    updateItem(itemId, { devices: [...item.devices, dev] });
  };

  const updateDevice = (itemId: string, devId: string, patch: Partial<QuoteDevice>) => {
    const item = quote.items.find(i => i.id === itemId);
    if (!item) return;
    updateItem(itemId, { devices: item.devices.map(d => d.id === devId ? { ...d, ...patch } : d) });
  };

  const removeDevice = (itemId: string, devId: string) => {
    const item = quote.items.find(i => i.id === itemId);
    if (!item) return;
    updateItem(itemId, { devices: item.devices.filter(d => d.id !== devId) });
  };

  const addPromo = () => {
    const promo: Promo = { id: uid(), name: 'New Promotion', type: 'monthly', value: 300, duration: 24, lines: 1 };
    update({ promos: [...quote.promos, promo] });
  };

  const updatePromo = (id: string, patch: Partial<Promo>) =>
    update({ promos: quote.promos.map(p => p.id === id ? { ...p, ...patch } : p) });

  const removePromo = (id: string) =>
    update({ promos: quote.promos.filter(p => p.id !== id) });

  const inputCls = 'w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 focus:border-purple-500/60 focus:ring-1 focus:ring-purple-500/20 text-white placeholder-gray-600 text-sm outline-none transition-all';
  const selectCls = inputCls + ' cursor-pointer';

  return (
    <div className="space-y-5">
      {/* Customer Info */}
      <Card>
        <h3 className="font-bold text-white text-sm mb-4 flex items-center gap-2">
          <div className="w-5 h-5 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400 text-[10px] font-black">1</div>
          Customer Information
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Customer Name</Label>
            <input
              value={quote.customerName}
              onChange={e => update({ customerName: e.target.value })}
              placeholder="John Smith"
              className={inputCls}
            />
          </div>
          <div>
            <Label>Company Name</Label>
            <input
              value={quote.companyName || ''}
              onChange={e => update({ companyName: e.target.value })}
              placeholder="Acme Corp"
              className={inputCls}
            />
          </div>
          <div>
            <Label>HQ State</Label>
            <select
              value={quote.hqState || ''}
              onChange={e => update({ hqState: e.target.value })}
              className={selectCls}
            >
              <option value="" className="bg-[#1A1A2E]">— Select State —</option>
              {US_STATES.map(s => (
                <option key={s} value={s} className="bg-[#1A1A2E]">{s}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Coverage Recommendation Badge */}
        {coverageRec && (
          <div className={`mt-4 p-3 rounded-xl bg-gradient-to-r ${coverageRec.color} bg-opacity-10 border border-white/10 flex items-start gap-3 animate-fade-in`}>
            <div className={`shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br ${coverageRec.color} flex items-center justify-center text-white text-xs font-black shadow-lg`}>
              📡
            </div>
            <div>
              <p className="text-xs font-bold text-white">
                Recommended Carrier for {quote.hqState}: <span className="text-emerald-400">{coverageRec.primary}</span>
              </p>
              <p className="text-[11px] text-gray-400 mt-0.5">{coverageRec.note}</p>
            </div>
          </div>
        )}

        <div className="mt-4">
          <Label>Agreement Term</Label>
          <div className="flex gap-2">
            {[1, 24, 30, 36].map(t => (
              <button
                key={t}
                onClick={() => update({ term: t })}
                className={`flex-1 py-2 rounded-xl text-sm font-semibold border transition-all duration-200 ${
                  quote.term === t
                    ? 'bg-purple-600/20 border-purple-500/40 text-purple-300'
                    : 'border-white/10 text-gray-500 hover:border-white/20 hover:text-gray-300'
                }`}
              >
                {t === 1 ? '1 Mo' : `${t} Mo`}
              </button>
            ))}
          </div>
        </div>
      </Card>


      {/* Seller Info */}
      <Card>
        <button
          onClick={() => setSellerOpen(!sellerOpen)}
          className="w-full flex items-center justify-between"
        >
          <h3 className="font-bold text-white text-sm flex items-center gap-2">
            <div className="w-5 h-5 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400 text-[10px] font-black">2</div>
            Seller Information
          </h3>
          {sellerOpen ? <ChevronUp size={16} className="text-gray-500" /> : <ChevronDown size={16} className="text-gray-500" />}
        </button>
        {sellerOpen && (
          <div className="mt-4 grid grid-cols-2 gap-4 animate-fade-in">
            {(
              [
                ['orgName', 'Organization', 'Your Company Name'],
                ['name',    'Your Name',    'Jane Doe'],
                ['title',   'Title',        'Business Solutions Rep'],
                ['email',   'Email',        'jane@example.com'],
                ['phone',   'Phone',        '(555) 000-0000'],
              ] as [keyof typeof quote.sellerInfo, string, string][]
            ).map(([key, label, ph]) => (
              <div key={key} className={key === 'orgName' ? 'col-span-2' : ''}>
                <Label>{label}</Label>
                <input
                  value={quote.sellerInfo[key]}
                  onChange={e => update({ sellerInfo: { ...quote.sellerInfo, [key]: e.target.value } })}
                  placeholder={ph}
                  className={inputCls}
                />
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Solution Items */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-white text-sm flex items-center gap-2">
            <div className="w-5 h-5 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400 text-[10px] font-black">3</div>
            Solutions
          </h3>
          <span className="text-xs text-gray-600">{quote.items.length} item{quote.items.length !== 1 ? 's' : ''}</span>
        </div>

        <div className="space-y-3">
          {quote.items.map((item, idx) => {
            const Icon    = TYPE_ICONS[item.type];
            const color   = TYPE_COLORS[item.type];
            const isOpen  = !collapsed[item.id];
            return (
              <div key={item.id} className="rounded-2xl bg-[#0D0D1A] border border-white/8 overflow-hidden">
                <div className="flex items-center gap-3 p-4">
                  <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center shrink-0 shadow-lg`}>
                    <Icon size={16} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-white">{item.type}</div>
                    <div className="text-xs text-gray-500">{item.planName} · {item.lines} line{item.lines !== 1 ? 's' : ''} · ${item.mrc * item.lines}/mo</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setCollapsed(c => ({ ...c, [item.id]: !c[item.id] }))}
                      className="p-1.5 rounded-lg text-gray-500 hover:text-gray-300 hover:bg-white/5 transition-all"
                    >
                      {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                    <button
                      onClick={() => removeItem(item.id)}
                      className="p-1.5 rounded-lg text-gray-600 hover:text-red-400 hover:bg-red-500/10 transition-all"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                {isOpen && (
                  <div className="px-4 pb-4 border-t border-white/5 pt-4 space-y-4 animate-fade-in">
                    {item.needsVerification && (
                      <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs">
                        <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                        <div>
                          <div className="font-bold mb-1">Pricing Unverified</div>
                          {item.verificationNote || "Could not verify this pricing via Google Search."}
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label>Solution Type</Label>
                        <select
                          value={item.type}
                          onChange={e => {
                            const t = e.target.value as SolutionType;
                            const p = PLAN_OPTIONS[t][0];
                            updateItem(item.id, { type: t, planName: p, mrc: DEFAULT_MRC[p] ?? 0 });
                          }}
                          className={selectCls}
                        >
                          {Object.values(SolutionType).map(t => (
                            <option key={t} value={t} className="bg-[#1A1A2E]">{t}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label>Plan</Label>
                        {item.type === SolutionType.FEATURES ? (
                          // Features always uses a free-form custom rate plan name
                          <input
                            autoFocus
                            value={item.planName}
                            onChange={e => updateItem(item.id, { planName: e.target.value })}
                            placeholder="Enter rate plan name..."
                            className={`${inputCls} border-sky-500/40 focus:border-sky-400/60 focus:ring-sky-500/20`}
                          />
                        ) : (
                          <>
                            <select
                              value={PLAN_OPTIONS[item.type].includes(item.planName) ? item.planName : '__CUSTOM__'}
                              onChange={e => {
                                if (e.target.value === '__CUSTOM__') {
                                  updateItem(item.id, { planName: '', mrc: 0 });
                                } else {
                                  updatePlan(item.id, e.target.value, item.type);
                                }
                              }}
                              className={selectCls}
                            >
                              {PLAN_OPTIONS[item.type].map(p => (
                                <option key={p} value={p} className="bg-[#1A1A2E]">{p}</option>
                              ))}
                              <option value="__CUSTOM__" className="bg-[#1A1A2E] text-amber-400">✏️ Custom Plan...</option>
                            </select>
                            {!PLAN_OPTIONS[item.type].includes(item.planName) && (
                              <input
                                autoFocus
                                value={item.planName}
                                onChange={e => updateItem(item.id, { planName: e.target.value })}
                                placeholder="Enter plan name..."
                                className={`${inputCls} mt-1.5 border-amber-500/40 focus:border-amber-400/60 focus:ring-amber-500/20`}
                              />
                            )}
                          </>
                        )}
                      </div>
                      <div>
                        <Label>MRC / Unit ($)</Label>
                        <input
                          type="number"
                          min={0}
                          value={item.mrc}
                          onChange={e => updateItem(item.id, { mrc: parseFloat(e.target.value) || 0 })}
                          className={inputCls}
                        />
                      </div>
                    </div>

                    {/* Lines count */}
                    <div>
                      <Label>Lines / Units</Label>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => updateItem(item.id, { lines: Math.max(1, item.lines - 1) })}
                          className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-white hover:border-purple-500/40 transition-all"
                        >
                          <Minus size={14} />
                        </button>
                        <input
                          type="number"
                          min={1}
                          value={item.lines}
                          onChange={e => updateItem(item.id, { lines: parseInt(e.target.value) || 1 })}
                          className="w-20 text-center px-2 py-1.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-purple-500/60"
                        />
                        <button
                          onClick={() => updateItem(item.id, { lines: item.lines + 1 })}
                          className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-white hover:border-purple-500/40 transition-all"
                        >
                          <Plus size={14} />
                        </button>
                        <span className="text-sm text-gray-500">
                          = <span className="text-white font-semibold">${(item.mrc * item.lines).toFixed(2)}</span>/mo
                        </span>
                      </div>
                    </div>

                    {/* Devices */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label>Device Add-ons</Label>
                        <button
                          onClick={() => addDevice(item.id)}
                          className="flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300 transition-colors"
                        >
                          <Plus size={12} /> Add Device
                        </button>
                      </div>
                      {item.devices.length > 0 ? (
                        <div className="space-y-2">
                          {item.devices.map(dev => {
                            const mrc = deviceMrcCalc(dev);
                            return (
                              <div key={dev.id} className="flex flex-wrap items-center gap-2 p-2.5 rounded-xl bg-white/3 border border-white/8">
                                <Package size={14} className="text-gray-500 shrink-0" />

                                {/* Device name */}
                                <div className="flex-1 min-w-[140px] flex flex-col gap-1">
                                  <select
                                    value={DEVICE_CATALOG.some(d => d.name === dev.name) ? dev.name : '__CUSTOM__'}
                                    onChange={e => {
                                      if (e.target.value === '__CUSTOM__') {
                                        updateDevice(item.id, dev.id, { name: '', cost: 0 });
                                      } else {
                                        const cat = DEVICE_CATALOG.find(d => d.name === e.target.value);
                                        updateDevice(item.id, dev.id, { name: e.target.value, cost: cat?.cost ?? dev.cost });
                                      }
                                    }}
                                    className="w-full bg-transparent text-xs text-gray-300 outline-none cursor-pointer"
                                  >
                                    {DEVICE_CATALOG.map(d => (
                                      <option key={d.name} value={d.name} className="bg-[#1A1A2E]">{d.name}</option>
                                    ))}
                                    <option value="__CUSTOM__" className="bg-[#1A1A2E] text-amber-400">✏️ Custom Device...</option>
                                  </select>
                                  {!DEVICE_CATALOG.some(d => d.name === dev.name) && (
                                    <input
                                      autoFocus
                                      value={dev.name}
                                      onChange={e => updateDevice(item.id, dev.id, { name: e.target.value })}
                                      placeholder="Enter device name..."
                                      className="w-full bg-white/5 border border-amber-500/40 rounded-lg px-2 py-1 text-xs text-amber-300 outline-none focus:border-amber-400/70 placeholder-gray-600"
                                    />
                                  )}
                                </div>

                                {/* Cost × Qty */}
                                <div className="flex items-center gap-1">
                                  <span className="text-xs text-gray-600">$</span>
                                  <input
                                    type="number"
                                    min={0}
                                    value={dev.cost}
                                    onChange={e => updateDevice(item.id, dev.id, { cost: parseFloat(e.target.value) || 0 })}
                                    className="w-16 text-xs text-center bg-white/5 border border-white/10 rounded-lg py-1 text-white outline-none"
                                  />
                                  <span className="text-xs text-gray-600">×</span>
                                  <input
                                    type="number"
                                    min={1}
                                    value={dev.count}
                                    onChange={e => updateDevice(item.id, dev.id, { count: parseInt(e.target.value) || 1 })}
                                    className="w-12 text-xs text-center bg-white/5 border border-white/10 rounded-lg py-1 text-white outline-none"
                                  />
                                </div>

                                {/* Financing term */}
                                <select
                                  value={dev.financingTerm}
                                  onChange={e => updateDevice(item.id, dev.id, { financingTerm: e.target.value as DeviceFinancingTerm })}
                                  className="bg-purple-500/10 border border-purple-500/20 rounded-lg px-2 py-1 text-xs text-purple-300 outline-none cursor-pointer"
                                  title="Financing term — divides total cost over months. FRP = Full Retail Price (no division)."
                                >
                                  {FINANCING_TERMS.map(ft => (
                                    <option key={ft} value={ft} className="bg-[#1A1A2E] text-white">
                                      {ft === 'FRP' ? 'FRP' : `${ft} Mo`}
                                    </option>
                                  ))}
                                </select>

                                {/* Live MRC result */}
                                <span className="text-xs font-semibold text-emerald-400 whitespace-nowrap">
                                  = ${mrc.toFixed(2)}/mo
                                </span>

                                <button
                                  onClick={() => removeDevice(item.id, dev.id)}
                                  className="text-gray-600 hover:text-red-400 transition-colors ml-auto"
                                >
                                  <X size={14} />
                                </button>
                                
                                {dev.needsVerification && (
                                  <div className="w-full mt-1 flex items-start gap-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs">
                                    <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                                    <span>
                                      <strong>Unverified:</strong> {dev.verificationNote || "Device price couldn't be confirmed."}
                                    </span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-700 italic">No devices added</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Add solution buttons */}
        <div className="mt-3 p-3 rounded-2xl border border-dashed border-white/10 bg-white/2">
          <p className="text-xs text-gray-600 text-center mb-3">Add a solution type</p>
          <div className="flex flex-wrap gap-2 justify-center">
            {Object.values(SolutionType).map(t => {
              const Icon = TYPE_ICONS[t];
              const color = TYPE_COLORS[t];
              return (
                <button
                  key={t}
                  onClick={() => addItem(t)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl border border-white/10 hover:border-purple-500/30 text-gray-400 hover:text-white text-xs font-medium transition-all hover:bg-white/5`}
                >
                  <div className={`w-5 h-5 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center`}>
                    <Icon size={11} className="text-white" />
                  </div>
                  {t}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Promos */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-white text-sm flex items-center gap-2">
            <div className="w-5 h-5 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400 text-[10px] font-black">4</div>
            Promotions &amp; Discounts
          </h3>
          <button
            onClick={addPromo}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-purple-500/10 border border-purple-500/20 text-purple-400 hover:bg-purple-500/20 text-xs font-semibold transition-all"
          >
            <Plus size={12} /> Add Promo
          </button>
        </div>

        {quote.promos.length === 0 ? (
          <p className="text-xs text-gray-700 text-center py-2 italic">No promotions added</p>
        ) : (
          <div className="space-y-3">
            {quote.promos.map(promo => {
              const effectiveMonthly = promoMonthlyCalc(promo);
              const totalValue       = promo.value * (promo.lines ?? 1);
              return (
                <div key={promo.id} className="rounded-xl bg-white/3 border border-white/8 overflow-hidden">
                  {/* Row 1: name + type */}
                  <div className="flex items-center gap-2 px-3 pt-3 pb-2">
                    <input
                      value={promo.name}
                      onChange={e => updatePromo(promo.id, { name: e.target.value })}
                      className="flex-1 bg-transparent text-sm text-white outline-none placeholder-gray-600 font-medium"
                      placeholder="Promo name"
                    />
                    <select
                      value={promo.type}
                      onChange={e => updatePromo(promo.id, { type: e.target.value as 'monthly' | 'onetime' })}
                      className="bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-xs text-gray-300 outline-none cursor-pointer"
                    >
                      <option value="monthly" className="bg-[#1A1A2E]">Monthly</option>
                      <option value="onetime" className="bg-[#1A1A2E]">One-time</option>
                    </select>
                    <button onClick={() => removePromo(promo.id)} className="text-gray-600 hover:text-red-400 transition-colors">
                      <Trash2 size={14} />
                    </button>
                  </div>

                  {/* Row 2: formula inputs + live result */}
                  <div className="flex flex-wrap items-center gap-1.5 px-3 pb-3 border-t border-white/5 pt-2">
                    {/* Amount per line */}
                    <span className="text-xs text-gray-600">-$</span>
                    <input
                      type="number"
                      min={0}
                      value={promo.value}
                      onChange={e => updatePromo(promo.id, { value: parseFloat(e.target.value) || 0 })}
                      className="w-20 text-center text-xs bg-white/5 border border-white/10 rounded-lg py-1 text-emerald-400 outline-none font-semibold"
                      title="Discount amount per line"
                    />

                    {/* Lines multiplier */}
                    <span className="text-xs text-gray-600">×</span>
                    <input
                      type="number"
                      min={1}
                      value={promo.lines ?? 1}
                      onChange={e => updatePromo(promo.id, { lines: parseInt(e.target.value) || 1 })}
                      className="w-14 text-center text-xs bg-white/5 border border-white/10 rounded-lg py-1 text-gray-300 outline-none"
                      title="Number of lines / units"
                    />
                    <span className="text-xs text-gray-600">lines</span>

                    {/* Total value display */}
                    <span className="text-xs text-gray-600">= -${totalValue.toFixed(2)}</span>

                    {/* Duration divisor (monthly only) */}
                    {promo.type === 'monthly' && (
                      <>
                        <span className="text-xs text-gray-600">÷</span>
                        <input
                          type="number"
                          min={1}
                          value={promo.duration ?? 24}
                          onChange={e => updatePromo(promo.id, { duration: parseInt(e.target.value) || 1 })}
                          className="w-14 text-center text-xs bg-white/5 border border-white/10 rounded-lg py-1 text-gray-400 outline-none"
                          title="Spread over how many months"
                        />
                        <span className="text-xs text-gray-600">mo</span>
                      </>
                    )}

                    {/* Live calculated result */}
                    <span className={`ml-auto text-xs font-bold px-2 py-1 rounded-lg ${
                      promo.type === 'monthly'
                        ? 'bg-emerald-500/15 text-emerald-400'
                        : 'bg-blue-500/15 text-blue-400'
                    }`}>
                      {promo.type === 'monthly'
                        ? `= -$${effectiveMonthly.toFixed(2)}/mo`
                        : `= -$${totalValue.toFixed(2)} one-time`
                      }
                    </span>
                  </div>

                  {promo.needsVerification && (
                    <div className="px-3 pb-3">
                      <div className="flex items-start gap-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs">
                        <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                        <span>
                          <strong>Unverified:</strong> {promo.verificationNote || "Promotion couldn't be confirmed."}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};

const X: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);
